import type { Entitlement } from "./access.ts";
import type { GrantKey } from "./module.ts";
import { SQUASH_AFTER_DAYS } from "./retention.ts";

export const LIMIT_HISTORY_VIEW_DAYS = "retention.days";

export const EVENT_JOURNAL_DAYS = 30;

export const JOURNAL_HOT_DAYS = 2;

export const EVENT_JOURNAL_MAX_DAYS = JOURNAL_HOT_DAYS + 1;

export const LIMIT_RATE_PER_MINUTE = "rate.per-minute";

export const LIMIT_SAVED_VIEWS = "views.max";

export const LIMIT_LEVELING_RULES = "leveling.rules.max";

export const LIMIT_PERKS = "leveling.perks.max";

export const LIMIT_PERK_CONDITIONS = "leveling.perks.conditions.max";

export const LIMIT_ANNOTATION_RULES = "annotations.rules.max";

export const LIMIT_IMAGES = "images.max";

export const IMAGES_HELD = 25;

export const MAX_SAVED_VIEW_NAME = 64;

export const SKU_KINDS = ["tier", "module", "addon"] as const;

export const PRICE_INTERVALS = ["month", "year"] as const;

type SkuKind = (typeof SKU_KINDS)[number];

export const FREE_SKU = "free";

export const TIER_PRO_SKU = "tier.pro";

export const TITAN_S_SKU = "tier.titan.s";

export const TITAN_M_SKU = "tier.titan.m";

export const TITAN_L_SKU = "tier.titan.l";

export const TITAN_SKUS = [TITAN_S_SKU, TITAN_M_SKU, TITAN_L_SKU] as const;

const TIER_SKUS = [FREE_SKU, TIER_PRO_SKU, ...TITAN_SKUS] as const;

export type TierSku = (typeof TIER_SKUS)[number];

const TIER_RANK: Readonly<Record<TierSku, number>> = {
  [FREE_SKU]: 0,
  [TIER_PRO_SKU]: 1,
  [TITAN_S_SKU]: 2,
  [TITAN_M_SKU]: 2,
  [TITAN_L_SKU]: 2,
};

export const TITAN_BANDS: readonly { readonly sku: string; readonly maxMembers: number | null }[] =
  [
    { sku: TITAN_S_SKU, maxMembers: 10_000 },
    { sku: TITAN_M_SKU, maxMembers: 50_000 },
    { sku: TITAN_L_SKU, maxMembers: null },
  ];

const SOLD_YEARLY: ReadonlySet<string> = new Set([TIER_PRO_SKU]);

export const isSoldYearly = (sku: string): boolean => SOLD_YEARLY.has(sku);

export const titanBandFor = (members: number): string => {
  for (const band of TITAN_BANDS) {
    if (band.maxMembers === null || members <= band.maxMembers) return band.sku;
  }
  throw new Error("the last titan band is unbounded");
};

export const CUSTOM_INSTANCE_GRANT = "instance.custom";

export const HISTORY_VIEW_UNLIMITED_GRANT = "retention.unlimited";

export const ANNOTATIONS_GRANT = "annotations.manual";

export const ANNOTATION_RULES_UNLIMITED_GRANT = "annotations.rules.unlimited";

export const BACKFILL_GRANT = "backfill.history";

export const TEAM_POLICIES_GRANT = "team.policies";

export const TEAM_ROLE_POLICIES_GRANT = "team.policies.roles";

export const TEAM_CUSTOM_POLICIES_GRANT = "team.policies.custom";

export const TEAM_POLICIES_UNLIMITED_GRANT = "team.policies.unlimited";

export const LIMIT_TEAM_POLICIES = "team.policies.max";

export const AUDIT_HISTORY_GRANT = "audit.history";

export const PULSE_GRANT = "pulse.readout";

export const INSIGHTS_FINDINGS_GRANT = "insights.findings";

export const INSIGHTS_EXPERIMENTS_GRANT = "insights.experiments";

export const LIMIT_BRIEFING_ITEMS = "insights.briefing.items";

export const BRIEFING_ITEMS_MAX = 10;

export const LIMIT_BRIEFING_WEEKS = "insights.briefing.weeks";

export const BRIEFING_WEEKS_UNLIMITED_GRANT = "insights.briefing.weeks.unlimited";

export const INSIGHTS_INBOX_GRANT = "insights.inbox";

export const INSIGHTS_ALERTS_GRANT = "insights.alerts";

export const GIVEAWAYS_GRANT = "giveaways";

export const GIVEAWAYS_REPEAT_GRANT = "giveaways.repeat";

export const LEVELING_GRANT = "leveling";

export const LEVELING_ADVANCED_GRANT = "leveling.advanced";

export const LEVELING_RULES_UNLIMITED_GRANT = "leveling.rules.unlimited";

export const PERKS_UNLIMITED_GRANT = "leveling.perks.unlimited";

export const LEVELING_RECOUNT_GRANT = "leveling.recount";

export const LEVELING_MEMBER_CARDS_GRANT = "leveling.member-cards";

export const LEVELING_REWARD_CARDS_GRANT = "leveling.reward-cards";

export const ENTITLEMENT_STATUSES = ["active", "grace", "canceled", "revoked"] as const;

export type EntitlementStatus = (typeof ENTITLEMENT_STATUSES)[number];

export const ENTITLED_STATUSES = [
  "active",
  "grace",
] as const satisfies readonly EntitlementStatus[];

const ENTITLED: ReadonlySet<string> = new Set(ENTITLED_STATUSES);

export const isEntitledStatus = (status: string): boolean => ENTITLED.has(status);

export interface HoldingTerm {
  readonly status: string;
  readonly currentPeriodEnd: Date | null;
  readonly graceUntil: Date | null;
}

const holdingEndsAt = (holding: HoldingTerm): Date | null => {
  const { currentPeriodEnd, graceUntil } = holding;
  if (holding.status !== "grace" || graceUntil === null) return currentPeriodEnd;
  return currentPeriodEnd === null || graceUntil < currentPeriodEnd ? graceUntil : currentPeriodEnd;
};

export const isHeldAt = (holding: HoldingTerm, now: Date): boolean => {
  if (!isEntitledStatus(holding.status)) return false;
  const end = holdingEndsAt(holding);
  return end === null || end > now;
};

const DUNNING_DAY_MS = 24 * 60 * 60 * 1_000;

export const DUNNING_RETRY_INTERVALS_MS: readonly number[] = [2, 5, 7, 7].map(
  (days) => days * DUNNING_DAY_MS,
);

export const PAST_DUE_DEADLINE_MS =
  DUNNING_RETRY_INTERVALS_MS.reduce((sum, interval) => sum + interval, 0) + 60_000;

export const graceUntilFrom = (unpaidSince: Date): Date =>
  new Date(unpaidSince.getTime() + PAST_DUE_DEADLINE_MS);

export type GrantValue = boolean | number;

export interface CatalogGrant {
  readonly sku: string;
  readonly grantKey: string;
  readonly value: GrantValue;
}

export const asGrantValue = (value: unknown): GrantValue | null =>
  typeof value === "boolean" || typeof value === "number" ? value : null;

export const NO_ENTITLEMENT: Entitlement = { grants: [], limits: {} };

export const resolveEntitlement = (
  catalog: readonly CatalogGrant[],
  entitledSkus: readonly string[],
): Entitlement => {
  const held = new Set([FREE_SKU, ...entitledSkus]);
  const grants = new Set<string>();
  const limits: Record<string, number> = {};
  for (const row of catalog) {
    if (!held.has(row.sku)) continue;
    if (row.value === true) {
      grants.add(row.grantKey);
      continue;
    }
    if (typeof row.value === "number") {
      const previous = limits[row.grantKey];
      limits[row.grantKey] = previous === undefined ? row.value : Math.max(previous, row.value);
    }
  }
  return { grants: [...grants].sort(), limits };
};

export interface Holding extends HoldingTerm {
  readonly sku: string;
}

export const entitlementAt = (
  catalog: readonly CatalogGrant[],
  holdings: readonly Holding[],
  now: Date,
): Entitlement =>
  resolveEntitlement(
    catalog,
    holdings.filter((holding) => isHeldAt(holding, now)).map((holding) => holding.sku),
  );

export interface CatalogSkuSeed {
  readonly sku: string;
  readonly kind: SkuKind;
  readonly rank: number;
  readonly grants: readonly { readonly key: GrantKey; readonly value: GrantValue }[];
}

const PRO_GRANTS: readonly { readonly key: GrantKey; readonly value: GrantValue }[] = [
  { key: LIMIT_HISTORY_VIEW_DAYS, value: 365 },
  { key: LIMIT_RATE_PER_MINUTE, value: 2_000 },
  { key: LIMIT_SAVED_VIEWS, value: 25 },
  { key: LIMIT_LEVELING_RULES, value: 20 },
  { key: LIMIT_PERKS, value: 20 },
  { key: LIMIT_PERK_CONDITIONS, value: 3 },
  { key: LEVELING_MEMBER_CARDS_GRANT, value: true },
  { key: LIMIT_ANNOTATION_RULES, value: 10 },
  { key: LIMIT_IMAGES, value: IMAGES_HELD },
  { key: LEVELING_GRANT, value: true },
  { key: LEVELING_ADVANCED_GRANT, value: true },
  { key: ANNOTATIONS_GRANT, value: true },
  { key: BACKFILL_GRANT, value: true },
  { key: TEAM_POLICIES_GRANT, value: true },
  { key: TEAM_ROLE_POLICIES_GRANT, value: true },
  { key: LIMIT_TEAM_POLICIES, value: 10 },
  { key: AUDIT_HISTORY_GRANT, value: true },
  { key: PULSE_GRANT, value: true },
  { key: INSIGHTS_FINDINGS_GRANT, value: true },
  { key: LIMIT_BRIEFING_ITEMS, value: 5 },
  { key: LIMIT_BRIEFING_WEEKS, value: 12 },
  { key: INSIGHTS_INBOX_GRANT, value: true },
];

const TITAN_GRANTS: readonly { readonly key: GrantKey; readonly value: GrantValue }[] = [
  ...PRO_GRANTS.filter(
    (grant) =>
      grant.key !== LIMIT_RATE_PER_MINUTE &&
      grant.key !== LIMIT_SAVED_VIEWS &&
      grant.key !== LIMIT_LEVELING_RULES &&
      grant.key !== LIMIT_PERKS &&
      grant.key !== LIMIT_PERK_CONDITIONS &&
      grant.key !== LIMIT_ANNOTATION_RULES &&
      grant.key !== LIMIT_IMAGES &&
      grant.key !== LIMIT_BRIEFING_ITEMS,
  ),
  { key: LIMIT_RATE_PER_MINUTE, value: 5_000 },
  { key: LIMIT_SAVED_VIEWS, value: 200 },
  { key: LIMIT_IMAGES, value: 150 },
  { key: CUSTOM_INSTANCE_GRANT, value: true },
  { key: TEAM_CUSTOM_POLICIES_GRANT, value: true },
  { key: TEAM_POLICIES_UNLIMITED_GRANT, value: true },
  { key: HISTORY_VIEW_UNLIMITED_GRANT, value: true },
  { key: ANNOTATION_RULES_UNLIMITED_GRANT, value: true },
  { key: LEVELING_RULES_UNLIMITED_GRANT, value: true },
  { key: PERKS_UNLIMITED_GRANT, value: true },
  { key: LIMIT_PERK_CONDITIONS, value: 5 },
  { key: LEVELING_RECOUNT_GRANT, value: true },
  { key: LEVELING_REWARD_CARDS_GRANT, value: true },
  { key: INSIGHTS_EXPERIMENTS_GRANT, value: true },
  { key: LIMIT_BRIEFING_ITEMS, value: BRIEFING_ITEMS_MAX },
  { key: BRIEFING_WEEKS_UNLIMITED_GRANT, value: true },
  { key: INSIGHTS_ALERTS_GRANT, value: true },
];

export const DEFAULT_CATALOG: readonly CatalogSkuSeed[] = [
  {
    sku: FREE_SKU,
    kind: "tier",
    rank: TIER_RANK[FREE_SKU],
    grants: [
      { key: LIMIT_HISTORY_VIEW_DAYS, value: 90 },
      { key: LIMIT_RATE_PER_MINUTE, value: 1_000 },
      { key: LIMIT_SAVED_VIEWS, value: 3 },
      { key: LIMIT_TEAM_POLICIES, value: 3 },
      { key: LIMIT_LEVELING_RULES, value: 5 },
      { key: LIMIT_PERKS, value: 8 },
      { key: LIMIT_PERK_CONDITIONS, value: 1 },
      { key: LIMIT_ANNOTATION_RULES, value: 2 },
      { key: ANNOTATIONS_GRANT, value: true },
      { key: LEVELING_GRANT, value: true },
      { key: LIMIT_BRIEFING_ITEMS, value: 1 },
      { key: LIMIT_BRIEFING_WEEKS, value: 1 },
    ],
  },
  {
    sku: TIER_PRO_SKU,
    kind: "tier",
    rank: TIER_RANK[TIER_PRO_SKU],
    grants: PRO_GRANTS,
  },
  ...TITAN_SKUS.map((sku) => ({
    sku,
    kind: "tier" as SkuKind,
    rank: TIER_RANK[sku],
    grants: TITAN_GRANTS,
  })),
];

const TIERS: ReadonlySet<string> = new Set(TIER_SKUS);

export const isTierSku = (sku: string): sku is TierSku => TIERS.has(sku);

export const rankOf = (sku: string): number => (isTierSku(sku) ? TIER_RANK[sku] : 0);

export const tierOf = (skus: readonly string[]): string | null => {
  const tiers = skus.filter(isTierSku);
  const [only] = tiers;
  return tiers.length === 1 && only !== undefined ? only : null;
};

export const catalogGrantsOf = (
  catalog: readonly CatalogSkuSeed[] = DEFAULT_CATALOG,
): readonly CatalogGrant[] =>
  catalog.flatMap((seed) =>
    seed.grants.map((grant) => ({ sku: seed.sku, grantKey: grant.key, value: grant.value })),
  );

const FREE_LIMITS: Readonly<Record<string, number>> = resolveEntitlement(
  catalogGrantsOf(),
  [],
).limits;

export const entitledLimit = (entitlement: Entitlement, key: string): number | undefined => {
  const held = entitlement.limits[key];
  return held === undefined ? FREE_LIMITS[key] : held;
};

const ceilingUnless = (entitlement: Entitlement, key: string, unlimited: string): number | null => {
  if (entitlement.grants.includes(unlimited)) return null;
  const held = entitledLimit(entitlement, key);
  return held === undefined ? null : held;
};

export const levelingRuleCeiling = (entitlement: Entitlement): number | null =>
  ceilingUnless(entitlement, LIMIT_LEVELING_RULES, LEVELING_RULES_UNLIMITED_GRANT);

export const perkCeiling = (entitlement: Entitlement): number | null =>
  ceilingUnless(entitlement, LIMIT_PERKS, PERKS_UNLIMITED_GRANT);

export const perkConditionCeiling = (entitlement: Entitlement): number => {
  const held = entitledLimit(entitlement, LIMIT_PERK_CONDITIONS);
  return held === undefined ? 1 : held;
};

export const annotationRuleCeiling = (entitlement: Entitlement): number | null =>
  ceilingUnless(entitlement, LIMIT_ANNOTATION_RULES, ANNOTATION_RULES_UNLIMITED_GRANT);

export const imagesHeld = (entitlement: Entitlement): number => {
  const held = entitledLimit(entitlement, LIMIT_IMAGES);
  return held === undefined ? IMAGES_HELD : held;
};

const CARD_ASSETS_PER_IMAGE = 4;

export const cardAssetsHeld = (entitlement: Entitlement): number =>
  imagesHeld(entitlement) * CARD_ASSETS_PER_IMAGE;

export const historyViewDays = (entitlement: Entitlement): number | undefined =>
  entitlement.grants.includes(HISTORY_VIEW_UNLIMITED_GRANT)
    ? undefined
    : entitledLimit(entitlement, LIMIT_HISTORY_VIEW_DAYS);

export const hourlyDays = (entitlement: Entitlement): number => {
  const days = historyViewDays(entitlement);
  return days === undefined ? SQUASH_AFTER_DAYS : Math.min(days, SQUASH_AFTER_DAYS);
};

export const briefingItems = (entitlement: Entitlement): number => {
  const held = entitledLimit(entitlement, LIMIT_BRIEFING_ITEMS);
  if (held === undefined) throw new Error("the free catalog must define the briefing item limit");
  return held;
};

export const briefingWeeksKept = (entitlement: Entitlement): number | null => {
  if (entitlement.grants.includes(BRIEFING_WEEKS_UNLIMITED_GRANT)) return null;
  const held = entitledLimit(entitlement, LIMIT_BRIEFING_WEEKS);
  if (held === undefined) throw new Error("the free catalog must define the briefing week limit");
  return held;
};

export const teamPolicyAccess = (
  entitlement: Entitlement,
): { readonly max: number | null; readonly roles: boolean; readonly custom: boolean } => {
  const { grants } = entitlement;
  const max = entitledLimit(entitlement, LIMIT_TEAM_POLICIES);
  if (max === undefined) throw new Error("the free catalog must define the team policy limit");
  return {
    max: grants.includes(TEAM_POLICIES_UNLIMITED_GRANT) ? null : max,
    roles: grants.includes(TEAM_ROLE_POLICIES_GRANT),
    custom: grants.includes(TEAM_CUSTOM_POLICIES_GRANT),
  };
};
